import { Injectable } from '@angular/core';
import { FuseMockApiService, FuseMockApiUtils } from '@fuse/lib/mock-api';
import { user as userData, appUsers as appUsersData } from 'app/mock-api/common/user/data';
import { assign, cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class UserMockApi {
    private _user: any = userData;
    private _appUsers: any[] = cloneDeep(appUsersData);

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ User - GET
        // -----------------------------------------------------------------------------------------------------
        // Get Fuse template's user
        this._fuseMockApiService
            .onGet('api/common/user')
            .reply(() => [200, cloneDeep(this._user)]);
        // Get App's users
        this._fuseMockApiService
            .onPatch('api/common/user')
            .reply(({ request }) => {
                const user = cloneDeep(request.body.user);
                this._user = assign({}, this._user, user);
                return [200, cloneDeep(this._user)];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ User - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/common/user')
            .reply(({ request }) => {
                // Get the user mock-api
                const user = cloneDeep(request.body.user);

                // Update the user mock-api
                this._user = assign({}, this._user, user);

                // Return the response
                return [200, cloneDeep(this._user)];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ App Users - GET
        // -----------------------------------------------------------------------------------------------------
        // GET all
        this._fuseMockApiService
            .onGet('api/common/app-users/all')
            .reply(() => {
                const users = cloneDeep(this._appUsers);
                users.sort((a, b) => {
                    const aKey = `${a.lastName ?? ''}${a.firstName ?? ''}${a.email ?? ''}`.toLowerCase();
                    const bKey = `${b.lastName ?? ''}${b.firstName ?? ''}${b.email ?? ''}`.toLowerCase();
                    return aKey.localeCompare(bKey);
                });
                return [200, users];
            });

        // SEARCH
        this._fuseMockApiService
            .onGet('api/common/app-users/search')
            .reply(({ request }) => {
                const query = (request.params.get('query') ?? '').toLowerCase().trim();

                let users = cloneDeep(this._appUsers);

                if (query) {
                    users = users.filter((u) => {
                        const name = `${u.displayName ?? ''} ${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
                        const email = (u.email ?? '').toLowerCase();
                        const role = (u.role ?? '').toLowerCase();
                        const depts = (u.departmentIds ?? []).join(' ').toLowerCase();
                        return name.includes(query) || email.includes(query) || role.includes(query) || depts.includes(query);
                    });
                }

                users.sort((a, b) => {
                    const aKey = `${a.lastName ?? ''}${a.firstName ?? ''}${a.email ?? ''}`.toLowerCase();
                    const bKey = `${b.lastName ?? ''}${b.firstName ?? ''}${b.email ?? ''}`.toLowerCase();
                    return aKey.localeCompare(bKey);
                });

                return [200, users];
            });

        // GET by uid
        this._fuseMockApiService
            .onGet('api/common/app-users/app-user')
            .reply(({ request }) => {
                const uid = request.params.get('uid');
                const user = cloneDeep(this._appUsers).find((u) => u.uid === uid) ?? null;
                return [200, user];
            });

        // CREATE
        this._fuseMockApiService
            .onPost('api/common/app-users/app-user')
            .reply(() => {
                const newUser = {
                    uid: FuseMockApiUtils.guid(),
                    firstName: 'New',
                    lastName: 'User',
                    displayName: 'New User',
                    email: 'new.user@company.com',
                    role: 'field_staff',
                    departmentIds: [],
                    isActive: true,
                    isSupportUser: false,
                    createdAt: Date.now(),
                    createdBy: 'system',
                };

                this._appUsers.unshift(newUser);
                return [200, cloneDeep(newUser)];
            });

        // UPDATE
        this._fuseMockApiService
            .onPatch('api/common/app-users/app-user')
            .reply(({ request }) => {
                const uid = request.body.uid;
                const changes = cloneDeep(request.body.user);

                let updated = null;

                this._appUsers.forEach((u, idx, arr) => {
                    if (u.uid === uid) {
                        arr[idx] = assign({}, arr[idx], changes, {
                            updatedAt: Date.now(),
                            updatedBy: 'system',
                        });
                        updated = arr[idx];
                    }
                });

                return [200, cloneDeep(updated)];
            });

        // TOGGLE ACTIVE
        this._fuseMockApiService
            .onPatch('api/common/app-users/toggle-active')
            .reply(({ request }) => {
                const uid = request.body.uid;

                let updated = null;

                this._appUsers.forEach((u, idx, arr) => {
                    if (u.uid === uid) {
                        arr[idx] = assign({}, arr[idx], {
                            isActive: !arr[idx].isActive,
                            updatedAt: Date.now(),
                            updatedBy: 'system',
                        });
                        updated = arr[idx];
                    }
                });

                return [200, cloneDeep(updated)];
            });

    }
}
